import React, { useEffect, useState } from "react";
import {
  selectIsUserProfile,
  selectPrimaryWheel,
} from "../../../redux/reducers/gallery";
import { useDispatch, useSelector } from "react-redux";
import { colors } from "../../../utils";
import { ASSETS } from "../../../utils/config";
import { isUserLoggedIn, transformedText } from "../../../utils/helpers";
import { SocialIcons } from "../../../utils/socialHelpers";
import Button from "../../Button";
import TextView from "../../TextView";
import UserImage from "../../UserImage";
import GallerySettings from "../GallerySettings";
import { ProfilePopProps } from "./ProfilePopup";
import {
  CardContainer,
  DescriptionWrapper,
  ProfileImageWrapper,
  Row,
  SocialLinkItem,
  SocialLinksWrapper,
} from "./style";
import { FaEllipsisV } from "react-icons/fa";
import Edit from "@assets/images/editIcon.svg";
import {
  Box,
  Fade,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Avatar,
  AvatarGroup,
  Modal,
} from "@mui/material";
import FlagIcon from "@mui/icons-material/Flag";
import {
  setReportInstance,
  setReportLocation,
  setReportModalOpen,
} from "../../../redux/actions";
import { isEmpty } from "lodash";
import { ReactComponent as LinkVaultIcon } from "@assets/images/LinkVault.svg";
import ShareIcon from "@mui/icons-material/Share";
import { toast } from "react-toastify";
import { useLocation, useParams } from "react-router-dom";
import LinkVaultPreview from "../GallerySettings/children/LinkVault/children/LinkVaultPreview";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import IyugaTooltip from "../../IyugaTooltip";
import WheelComponent from "../../WheelComponent";
import { openNonAuthenticatedModal } from "../../../redux/reducers/nonAuthenticatedModal";
import API from "../../../services/request";
import { API_PATH } from "../../../utils/config";

const Profile = ({ data, wheelUpdatedDataSorted }) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const [isSettingOpen, setIsSettingOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const isUserProfile = useSelector(selectIsUserProfile);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [settingsTabIndex, setSettingsTabIndex] = useState("0");
  const { id } = useParams(); // This will get the current user's ID from URL if needed

  const bboxData = useSelector((state) => {
    if (!isEmpty(state.gallery.profile?.business_box)) {
      return state.gallery?.profile?.business_box;
    } else return null;
  });

  const searchParams = new URLSearchParams(location.search);
  const isLinkVault = searchParams.get("view") === "linkvault";

  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(
    isLinkVault ?? false
  );

  const linksToShow = profile?.external_links?.filter(
    (link) => link.is_cta_enabled
  );

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleReport = () => {
    const isLoggedIn = isUserLoggedIn();

    if (!isLoggedIn) {
      dispatch(
        openNonAuthenticatedModal({
          title: "Report User?",
          action: "report",
          type: "user",
          headerIcon: InfoOutlinedIcon,
          headerIconProps: {
            sx: { color: "#E54D2E", width: "24px", height: "24px" },
          },
        })
      );
    } else {
      dispatch(setReportModalOpen(true));
      dispatch(setReportInstance(data?.user_id));
      dispatch(setReportLocation("USER"));
    }
    handleClose();
  };

  const handleEditClick = () => {
    setSettingsTabIndex("0");
    setIsSettingOpen(true);
    handleClose();
  };

  const handleLinkVaultClick = () => {
    setSettingsTabIndex("1");
    setIsSettingOpen(true);
  };

  const handleShare = async () => {
    try {
      // Get the base URL (either localhost or production URL)
      const baseUrl = window.location.origin;

      // Create the LinkVault URL
      const linkVaultUrl = `${baseUrl}/gallery/${id}?view=linkvault`;

      // Copy to clipboard using the Clipboard API
      await navigator.clipboard.writeText(linkVaultUrl);

      // Show success toast
      toast.success("LinkVault URL copied!", {
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      console.error("Failed to copy URL:", error);
      toast.error("Failed to copy URL");
    }
  };

  const handleLinkVaultPreview = () => {
    if (isUserProfile) {
      handleLinkVaultClick();
    } else {
      setIsPreviewModalOpen(true);
    }
  };

  useEffect(() => {
    if (data) {
      console.log("data", data);
      setProfile(data);
      // if profile contains follower counts, use it as initial value
      setFollowerCount(data?.followers ?? 0);
      if (ProfilePopProps?.handleDataChange) {
        ProfilePopProps.handleDataChange(data);
      } else {
        console.error("handleDataChange is not defined");
      }
    }
  }, [data]);

  // fetch follow status once on mount (use id from params)
  useEffect(() => {
    if (isUserProfile) return;
    const fetchFollowStatus = async () => {
      if (!id) return;
      try {
        const res = await API.get(API_PATH.FOLLOW_STATUS(id));
        const d = res?.data || {};
        setIsFollowing(Boolean(d.is_following));
        setFollowerCount(d.follower_count ?? followerCount);
      } catch (err) {
        // ignore
      }
    };

    fetchFollowStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleFollow = async () => {
    if (!isUserLoggedIn()) {
      dispatch(
        openNonAuthenticatedModal({
          title: "Follow User?",
          action: "follow",
          type: "user",
          headerIcon: InfoOutlinedIcon,
          headerIconProps: {
            sx: { color: "#9B5AF8", width: "24px", height: "24px" },
          },
          referralCode: profile?.referral_code,
        })
      );
      return;
    }

    if (!id) return;

    try {
      setFollowLoading(true);
      const path = isFollowing
        ? API_PATH.UNFOLLOW_USER(id)
        : API_PATH.FOLLOW_USER(id);
      const res = await API.post(path);
      const message = res?.data?.message;
      if (message) toast.success(message);
      // toggle local state
      setIsFollowing((prev) => !prev);
      setFollowerCount((c) =>
        isFollowing ? Math.max(0, c - 1) : (c || 0) + 1
      );
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || "Failed to update follow status"
      );
    } finally {
      setFollowLoading(false);
    }
  };

  const onClickIcon = (link) => {
    let url = link.trim();

    if (!/^https?:\/\//i.test(url)) {
      url = `https://${url}`;
    }

    window.open(url, "_blank");
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "340px",
    maxHeight: "80vh",
    overflowY: "auto",
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: "8px",
    border: "1px solid #E1E1E1",
  };

  const showFullBio = isEmpty(profile?.external_links) && !isUserProfile;

  // let test = `Lorem ipsum dolor sit amet consectetur adipisicing elit. Facilis molestias fuga ex eius culpa iure. Delectus, obcaecati pariatur quaerat voluptate rerum dicta numquam, nam, voluptatum tempore officiis expedita est et?
  // Molestiae fugiat neque, ratione, hic, officia exercitationem deserunt culpa ex similique praesentium nesciunt facere animi qui sapiente at accusamus maiores minus alias tenetur. Dicta, rerum a! Vero sed placeat iusto!
  // Quaerat molestias excepturi tempora soluta fuga facere ducimus corporis odio, omnis ab mollitia sint culpa pariatur impedit unde illo optio, qui aliquid. Sed architecto tempora, qui dolorum aut quidem aperiam.`;
  return (
    <>
      <CardContainer
        className="link-vault-button"
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          paddingBottom: "88px",
        }}
      >
        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Row>
            <Row>
              <ProfileImageWrapper>
                {wheelUpdatedDataSorted &&
                wheelUpdatedDataSorted?.length > 0 ? (
                  <WheelComponent
                    userData={{
                      id: Math.random(),
                      url: profile?.profile_image || ASSETS.iyugaLogo,
                    }}
                    maxSelectedArcs={0}
                    arcThickness={-10}
                    chartData={wheelUpdatedDataSorted}
                    margin={1}
                    setChartData={() => {}}
                    containerDivStyle={{
                      width: "100%",
                      height: "100%",
                      maxWidth: "82px",
                      maxHeight: "82px",
                    }}
                    config={{
                      showTooltips: false,
                      centerGap: -2,
                    }}
                  />
                ) : (
                  <UserImage size={60} image={profile?.profile_image} />
                )}
              </ProfileImageWrapper>
              <div>
                {profile?.username && (
                  <TextView
                    variant="notifName"
                    style={{
                      color: colors.primary,
                      marginBottom: 0,
                      textTransform: "capitalize",
                    }}
                  >
                    {profile?.username}
                  </TextView>
                )}

                <TextView
                  variant="largeText"
                  style={{ color: colors.primary, WebkitLineClamp: 2 }}
                >
                  {profile?.first_name} {profile?.last_name}
                </TextView>
              </div>
            </Row>

            <Box
              sx={{
                display: "flex",
                gap: "10px",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {!isUserProfile && (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Button
                    onClick={handleFollow}
                    isLoading={followLoading}
                    style={{
                      borderRadius: 8,
                      padding: "6px 12px",
                      textTransform: "none",
                    }}
                    loaderSize={16}
                  >
                    {isFollowing ? "Unfollow" : "Follow"}
                  </Button>
                </Box>
              )}

              <IconButton
                style={{ color: "#123051", width: "24px", height: "24px" }}
                onClick={(event) => {
                  setAnchorEl(event.currentTarget);
                }}
              >
                <FaEllipsisV
                  color="#565556"
                  style={{ cursor: "pointer", width: "8px", height: "16px" }}
                />
              </IconButton>
            </Box>

            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              TransitionComponent={Fade}
              timeout={{ enter: 100, exit: 300 }}
              PaperProps={{
                style: {
                  borderRadius: "8px",
                  width: "100px",
                  transition: "box-shadow 0.3s ease-in-out",
                  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                },
              }}
            >
              {isUserProfile && (
                <MenuItem
                  onClick={handleEditClick}
                  sx={{
                    fontSize: "14px",
                    color: "#333",
                    "&:hover": {
                      backgroundColor: "#f0f0f0",
                    },
                  }}
                >
                  <img
                    style={{ marginRight: "0.2rem" }}
                    src={Edit}
                    className="img-fluid"
                    alt="Edit"
                    width="15"
                    height="15"
                  />
                  Edit
                </MenuItem>
              )}

              {!isUserProfile && (
                <MenuItem
                  onClick={handleReport}
                  sx={{
                    fontSize: "14px",
                    color: "#333",
                    "&:hover": {
                      backgroundColor: "#f0f0f0",
                    },
                  }}
                >
                  <FlagIcon
                    sx={{
                      marginRight: "0.2rem",
                      width: "18px",
                      height: "18px",
                      color: "#BBBBBB",
                    }}
                  />
                  Report
                </MenuItem>
              )}
            </Menu>
          </Row>
          {/* Stats row: Wheels | Followers | Following */}
          <Box
            sx={{
              display: "flex",
              gap: 3,
              marginTop: 0,
              alignItems: "center",
              justifyContent: "flex-start",
            }}
          >
            <Box
              sx={{
                textAlign: "left",
                pr: 2,
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  right: 0,
                  top: "50%",
                  transform: "translateY(-50%)",
                  height: "60%",
                  width: "1px",
                  backgroundColor: "#E5E7EB",
                },
              }}
            >
              <TextView
                variant="subtitle"
                style={{ color: colors.primary, fontWeight: 700 }}
              >
                {profile?.number_of_wheels ?? 0}
              </TextView>
              <TextView
                variant="text"
                style={{ color: "#6B7280", fontWeight: 500 }}
              >
                Wheels
              </TextView>
            </Box>

            <Box
              sx={{
                textAlign: "left",
                pr: 2,
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  right: 0,
                  top: "50%",
                  transform: "translateY(-50%)",
                  height: "60%",
                  width: "1px",
                  backgroundColor: "#E5E7EB",
                },
              }}
            >
              <TextView
                variant="subtitle"
                style={{ color: colors.primary, fontWeight: 700 }}
              >
                {followerCount ?? profile?.followers ?? 0}
              </TextView>
              <TextView
                variant="text"
                style={{ color: "#6B7280", fontWeight: 500 }}
              >
                Followers
              </TextView>
            </Box>

            <Box sx={{ textAlign: "left" }}>
              <TextView
                variant="subtitle"
                style={{ color: colors.primary, fontWeight: 700 }}
              >
                {profile?.followings ?? 0}
              </TextView>
              <TextView
                variant="text"
                style={{ color: "#6B7280", fontWeight: 500 }}
              >
                Following
              </TextView>
            </Box>
          </Box>

          <Box
            sx={{
              position: "relative",
              maxHeight: showFullBio ? "115px" : "55px", // Adjust this value based on your needs
              marginTop: 0,
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "20px",

                background:
                  "linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.8) 49%, #FFFFFF 99%)",

                pointerEvents: "none",
              },
            }}
          >
            <Box
              sx={{
                overflowY: "auto",
                maxHeight: showFullBio ? "115px" : "55px", // Same as parent
                paddingRight: "8px", // Space for scrollbar
                "&::-webkit-scrollbar": {
                  width: "4px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#9B5AF8",
                  borderRadius: "4px",
                  "&:hover": {
                    background: "#81459B",
                  },
                },
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  fontFamily: "DM Sans",
                  // lineHeight: "20px",
                  paddingBottom: "40px", // Space for gradient fade
                }}
                dangerouslySetInnerHTML={{
                  __html: profile?.bio?.replace(
                    /<p/g,
                    '<p style="margin-bottom: 0; margin-top: 0;"'
                  ),
                }}
              />
            </Box>
          </Box>
        </div>

        <Row
          style={{
            position: "absolute", // ⬅️ anchor
            left: 16,
            right: 16,
            bottom: 8, // ⬅️ "a little above" the card bottom
          }}
        >
          {isEmpty(profile?.external_links) && isUserProfile ? (
            <Box
              sx={{
                width: "100%",
                border: "2px dashed #81459B",
                borderRadius: "7px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                cursor: "pointer",
                p: "10px 16px",
                gap: 2,
              }}
              onClick={handleLinkVaultClick}
            >
              <LinkVaultIcon style={{ height: "32px", width: "32px" }} />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  sx={{
                    color: "#565556",
                    fontSize: "16px",
                    fontWeight: "500",
                    fontFamily: "DM Sans",
                  }}
                >
                  Create LinkVault
                </Typography>
                <IyugaTooltip
                  tooltip="Showcase your social links here!"
                  placement="top"
                >
                  <InfoOutlinedIcon
                    sx={{
                      color: "#9B5AF8",
                      width: "24px",
                      height: "24px",
                      cursor: "pointer",
                    }}
                  />
                </IyugaTooltip>
              </Box>
            </Box>
          ) : !isEmpty(profile?.external_links) ? (
            <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
              <Box
                sx={{
                  flex: 1,
                  border: "1px solid #BBBBBB",
                  borderRadius: "7px",
                  display: "flex",
                  alignItems: "center",
                  p: "10px 12px",
                  gap: 2,
                  cursor: "pointer", // Always show pointer since it's clickable for all users
                  justifyContent: "space-between",
                }}
                onClick={handleLinkVaultPreview}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <LinkVaultIcon style={{ height: "24px", width: "24px" }} />
                  <Typography
                    sx={{
                      color: "#344054",
                      fontSize: "14px",
                      fontWeight: "400",
                      fontFamily: "DM Sans",
                    }}
                  >
                    My LinkVault
                  </Typography>
                </Box>

                <AvatarGroup
                  max={4}
                  total={linksToShow?.length}
                  renderSurplus={(surplus) => (
                    <Avatar
                      variant="circular"
                      sx={{
                        height: 28,
                        minWidth: surplus >= 100 ? 30 : surplus >= 10 ? 28 : 24, // base width
                        pl: surplus >= 100 ? 0.6 : surplus >= 10 ? 0.5 : 0.25, // add horizontal padding for 3+ digits
                        borderRadius: "9999px", // pill shape so it can grow horizontally
                        bgcolor: "#EFE9FE",
                        color: "#9B5AF8",
                        border: "2px solid #FFFFFF",
                        fontFamily: "DM Sans",
                        fontWeight: 700,
                        fontSize: surplus >= 100 ? 9 : surplus >= 10 ? 10 : 11,
                        lineHeight: 1,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {`+${surplus}`}
                    </Avatar>
                  )}
                  sx={{
                    "& .MuiAvatarGroup-avatar": {
                      width: 24,
                      height: 24,
                      fontSize: "14px",
                      fontWeight: "700",
                      fontFamily: "DM Sans",
                      background: "#F6F6F6",
                      border: "2px solid #FFFFFF", // Updated to match other avatars
                      color: "#9B5AF8",
                    },
                  }}
                >
                  {linksToShow.map((link, index) => (
                    <Avatar
                      key={index}
                      sx={{
                        bgcolor: "transparent",
                        border: "2px solid #FFFFFF",
                      }}
                      src={link.icon || ASSETS.website}
                    />
                  ))}
                </AvatarGroup>
              </Box>

              <Box
                sx={{
                  width: "52px",
                  border: "1px solid #BBBBBB",
                  borderRadius: "7px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  cursor: "pointer",
                  p: "10px 12px",
                }}
                onClick={handleShare}
              >
                <ShareIcon sx={{ color: "#565556" }} />
              </Box>
            </Box>
          ) : (
            <></>
          )}
        </Row>
      </CardContainer>
      <GallerySettings
        open={isSettingOpen}
        tabIdx={settingsTabIndex}
        onClose={() => {
          setIsSettingOpen(false);
          setSettingsTabIndex("0");
        }}
        openDefault={!isEmpty(bboxData)}
      />

      <Modal
        open={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        aria-labelledby="linkvault-preview-modal"
        aria-describedby="modal-showing-linkvault-preview"
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(86, 85, 86, 0.8)", // #565556 with 80% opacity
            },
          },
        }}
      >
        <Box sx={modalStyle}>
          <LinkVaultPreview
            showMenu
            handleEditLinkVault={handleLinkVaultClick}
            links={profile?.external_links}
          />
        </Box>
      </Modal>
    </>
  );
};

export default Profile;
